import { useState, useEffect, useCallback } from 'react'
import { apiUrl, authFetch } from '../../lib/api'

interface AuditLog { id: string; user_id: string; user_email: string; action: string; resource: string; details: string; ip_address: string; created_at: string }
interface SSOConfig { id?: string; provider: string; client_id: string; issuer_url: string; redirect_uri: string; enabled: boolean }
interface Stats { users: number; projects: number; deployments: number; auditLogs: number; recentLogins: number }

export function EnterprisePanel() {
  const [tab, setTab] = useState<'dashboard' | 'audit' | 'sso' | 'users'>('dashboard')

  return (
    <div style={panelStyle}>
      <div style={headerStyle}>
        <span>ENTERPRISE</span>
        <div style={{ display: 'flex', gap: 4 }}>
          {(['dashboard', 'audit', 'sso', 'users'] as const).map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              padding: '2px 8px', borderRadius: 4, fontSize: 9, fontFamily: 'var(--font-mono)', fontWeight: 700,
              border: tab === t ? '1px solid var(--accent)' : '1px solid var(--border-2)',
              background: tab === t ? 'rgba(124,110,245,0.1)' : 'none',
              color: tab === t ? 'var(--accent)' : 'var(--text-2)', cursor: 'pointer',
            }}>{t}</button>
          ))}
        </div>
      </div>

      {tab === 'dashboard' && <DashboardTab />}
      {tab === 'audit' && <AuditTab />}
      {tab === 'sso' && <SSOTab />}
      {tab === 'users' && <UsersTab />}
    </div>
  )
}

function DashboardTab() {
  const [stats, setStats] = useState<Stats | null>(null)

  useEffect(() => {
    authFetch(apiUrl('/api/enterprise/stats')).then(r => r.json()).then(setStats).catch(() => {})
  }, [])

  if (!stats) return <div style={{ padding: 20, color: 'var(--text-2)', fontSize: 11, textAlign: 'center' }}>Loading...</div>

  const cards = [
    { label: 'Total Users', value: stats.users, icon: '👥', color: 'var(--accent)' },
    { label: 'Projects', value: stats.projects, icon: '📁', color: 'var(--accent-2)' },
    { label: 'Deployments', value: stats.deployments, icon: '🚀', color: 'var(--accent-3)' },
    { label: 'Audit Events', value: stats.auditLogs, icon: '📋', color: 'var(--text-1)' },
    { label: 'Logins (7d)', value: stats.recentLogins, icon: '🔑', color: 'var(--accent-2)' },
  ]

  return (
    <div style={{ flex: 1, overflow: 'auto', padding: 12 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 8 }}>
        {cards.map(c => (
          <div key={c.label} style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
            <div style={{ fontSize: 16, marginBottom: 4 }}>{c.icon}</div>
            <div style={{ fontSize: 18, fontWeight: 900, fontFamily: 'var(--font-display)', color: c.color }}>{c.value}</div>
            <div style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>{c.label}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

function AuditTab() {
  const [logs, setLogs] = useState<AuditLog[]>([])
  const [total, setTotal] = useState(0)
  const [filter, setFilter] = useState('')
  const [actions, setActions] = useState<string[]>([])

  const load = useCallback(() => {
    const params = new URLSearchParams({ limit: '50' })
    if (filter) params.set('action', filter)
    authFetch(apiUrl(`/api/enterprise/audit?${params}`)).then(r => r.json()).then(data => {
      setLogs(data.logs ?? [])
      setTotal(data.total ?? 0)
    }).catch(() => {})
  }, [filter])

  useEffect(() => { load() }, [load])
  useEffect(() => {
    authFetch(apiUrl('/api/enterprise/audit/actions')).then(r => r.json()).then(setActions).catch(() => {})
  }, [])

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
        <select value={filter} onChange={e => setFilter(e.target.value)} style={inputStyle}>
          <option value="">All actions ({total})</option>
          {actions.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>
      <div style={{ flex: 1, overflow: 'auto' }}>
        {logs.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-2)', fontSize: 11 }}>No audit logs yet</div>
        ) : logs.map(log => (
          <div key={log.id} style={{ padding: '6px 12px', borderBottom: '1px solid var(--border)', fontSize: 11, fontFamily: 'var(--font-mono)' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ color: actionColor(log.action), fontWeight: 700 }}>{log.action}</span>
              {log.resource && <span style={{ color: 'var(--text-2)' }}>→ {log.resource}</span>}
              <span style={{ marginLeft: 'auto', fontSize: 9, color: 'var(--text-2)' }}>{new Date(log.created_at).toLocaleString()}</span>
            </div>
            <div style={{ fontSize: 9, color: 'var(--text-2)', marginTop: 2 }}>
              {log.user_email ?? log.user_id?.slice(0, 8)} {log.ip_address ? `· ${log.ip_address}` : ''}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function SSOTab() {
  const [config, setConfig] = useState<SSOConfig>({ provider: 'oidc', client_id: '', issuer_url: '', redirect_uri: '', enabled: false })
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState('')

  useEffect(() => {
    authFetch(apiUrl('/api/enterprise/sso')).then(r => r.json()).then(data => {
      if (data) setConfig(data)
    }).catch(() => {})
  }, [])

  const save = async () => {
    setSaving(true); setMsg('')
    try {
      const res = await authFetch(apiUrl('/api/enterprise/sso'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      })
      if (res.ok) setMsg('✓ Saved')
      else { const d = await res.json(); setMsg(`✗ ${d.error}`) }
    } catch (e: any) { setMsg(`✗ ${e.message}`) }
    finally { setSaving(false) }
  }

  return (
    <div style={{ flex: 1, overflow: 'auto', padding: 12 }}>
      <div style={{ fontSize: 11, color: 'var(--text-1)', fontFamily: 'var(--font-mono)', marginBottom: 12, lineHeight: 1.5 }}>
        Configure OIDC/SAML Single Sign-On for your organization. Users will be able to log in with their corporate identity provider.
      </div>
      <div style={fieldStyle}>
        <label style={labelStyle}>Provider</label>
        <select value={config.provider} onChange={e => setConfig({ ...config, provider: e.target.value })} style={inputStyle}>
          <option value="oidc">OpenID Connect (OIDC)</option>
          <option value="saml">SAML 2.0</option>
          <option value="google">Google Workspace</option>
          <option value="okta">Okta</option>
          <option value="azure">Azure AD</option>
        </select>
      </div>
      <div style={fieldStyle}>
        <label style={labelStyle}>Client ID</label>
        <input value={config.client_id} onChange={e => setConfig({ ...config, client_id: e.target.value })} placeholder="your-client-id" style={inputStyle} />
      </div>
      <div style={fieldStyle}>
        <label style={labelStyle}>Issuer URL</label>
        <input value={config.issuer_url} onChange={e => setConfig({ ...config, issuer_url: e.target.value })} placeholder="https://accounts.google.com" style={inputStyle} />
      </div>
      <div style={fieldStyle}>
        <label style={labelStyle}>Redirect URI</label>
        <input value={config.redirect_uri} onChange={e => setConfig({ ...config, redirect_uri: e.target.value })} placeholder="http://localhost:3001/api/enterprise/sso/callback" style={inputStyle} />
      </div>
      <div style={{ ...fieldStyle, display: 'flex', alignItems: 'center', gap: 8 }}>
        <input type="checkbox" checked={config.enabled} onChange={e => setConfig({ ...config, enabled: e.target.checked })} id="sso-enabled" />
        <label htmlFor="sso-enabled" style={{ fontSize: 11, color: 'var(--text-1)', fontFamily: 'var(--font-mono)', cursor: 'pointer' }}>Enable SSO</label>
      </div>
      {msg && <div style={{ fontSize: 10, color: msg.startsWith('✓') ? 'var(--accent-2)' : 'var(--red)', fontFamily: 'var(--font-mono)', marginBottom: 8 }}>{msg}</div>}
      <button onClick={save} disabled={saving} style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid var(--accent)', background: 'rgba(124,110,245,0.1)', color: 'var(--accent)', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700 }}>
        {saving ? 'Saving...' : 'Save SSO Config'}
      </button>
    </div>
  )
}

function UsersTab() {
  const [users, setUsers] = useState<any[]>([])

  useEffect(() => {
    authFetch(apiUrl('/api/enterprise/users')).then(r => r.json()).then(data => setUsers(Array.isArray(data) ? data : [])).catch(() => {})
  }, [])

  return (
    <div style={{ flex: 1, overflow: 'auto' }}>
      {users.length === 0 ? (
        <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-2)', fontSize: 11 }}>No users found</div>
      ) : users.map(u => (
        <div key={u.id} style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--bg-3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: 'var(--accent)', flexShrink: 0 }}>
            {(u.name ?? u.email ?? '?').charAt(0).toUpperCase()}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', fontWeight: 600, color: 'var(--text-0)' }}>{u.name ?? u.email}</div>
            <div style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>{u.email} · {u.plan ?? 'free'}</div>
          </div>
          <span style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>{new Date(u.created_at).toLocaleDateString()}</span>
        </div>
      ))}
    </div>
  )
}

function actionColor(action: string): string {
  if (action.includes('login')) return 'var(--accent-2)'
  if (action.includes('delete') || action.includes('remove')) return 'var(--red)'
  if (action.includes('create') || action.includes('deploy')) return 'var(--accent)'
  if (action.includes('update') || action.includes('config')) return 'var(--accent-3)'
  return 'var(--text-1)'
}

const panelStyle: React.CSSProperties = { width: '100%', height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg-1)', borderRight: '1px solid var(--border)', overflow: 'hidden' }
const headerStyle: React.CSSProperties = { padding: '8px 14px', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--text-2)', textTransform: 'uppercase', borderBottom: '1px solid var(--border)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }
const inputStyle: React.CSSProperties = { width: '100%', padding: '5px 8px', borderRadius: 4, border: '1px solid var(--border-2)', background: 'var(--bg-2)', color: 'var(--text-0)', fontFamily: 'var(--font-mono)', fontSize: 11, outline: 'none' }
const fieldStyle: React.CSSProperties = { marginBottom: 10 }
const labelStyle: React.CSSProperties = { display: 'block', fontSize: 9, fontWeight: 700, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginBottom: 4, letterSpacing: '0.05em', textTransform: 'uppercase' }
